import { getPlayer, validatePlayerExists } from '@/db/players';
import { getSessionEvents } from '@/db/session-events';
import { getSessionMetadata } from '@/db/sessions';
import { handleUserFacingError, handleBackgroundError } from '@/lib/error-handler';
import { subscribeToSession } from '@/lib/realtime';
import { syncSession } from '@/lib/sync';
import { mergeRemoteEvents } from '@/lib/sync/merge';
import {
  createRoundId,
  type GameConfig,
  type NewSessionEvent,
  type Player,
  type PlayerId,
  type PlayerOrTeamId,
  type RoundId,
  type Session,
  type SessionId,
  type SessionEvent,
  type UserId,
} from '@/schemas';
import { createImageId } from '@/schemas/ids';
import { createSessionEventActivityResolver } from '../event-activity';
import { replaySessionEvents } from '../event-replay';
import type { OperationQueue } from '../operation-queue';
import { assignPlayerColor } from '../player-utils';
import {
  addRoundTagToDraft,
  addSessionTagOnDraft,
  applyRoundNoteToDraft,
  clearActiveSessionOnDraft,
  removeRoundTagFromDraft,
  removeSessionTagOnDraft,
  restoreActiveSessionOnDraft,
  setRoundTagsOnDraft,
  setSessionGameConfigOnDraft,
  setSessionNameOnDraft,
  setSessionNotesOnDraft,
  setSessionTagsOnDraft,
  setSessionWinnerOnDraft,
} from '../session-draft-utils';
import {
  computeUndoRedoAvailability,
  createRequireActiveSession,
  createSystemEvent,
  ensureSessionStatus,
  findRoundOrThrow,
} from '../session-helpers';
import { getCurrentRound } from '../session-selectors';
import type { ImmerSetter, SessionStore } from '../session-store.types';
import { getRedoableEvent, getUndoableEvent } from '../undo-redo';
import { calculateWinner } from '../winner-calculation';
import {
  enqueueAddImage,
  enqueueRemoveImage,
  enqueueUpdateImageCaption,
  enqueueSetPrimaryImage,
} from './operations/images';
import {
  enqueueGenerateInviteToken,
  enqueueRevokeInviteToken,
  enqueueToggleInviteLink,
} from './operations/invites';
import {
  enqueueJoinPlayer,
  enqueueRemovePlayer,
  enqueueReorderPlayers,
} from './operations/players';
import { enqueueStartRound, enqueueCompleteRound } from './operations/rounds';
import { enqueueScoreDelta, enqueueScoreSet } from './operations/scores';
import {
  enqueueEndSession,
  enqueuePauseSession,
  enqueueResumeSession,
  enqueueRestartSession,
} from './operations/session';
import { enqueueSessionEventOperation } from './queue-utils';
import type { QueueDependencies } from './queue-utils';

type SessionStoreActions = Pick<
  SessionStore,
  | 'loadSession'
  | 'clearSession'
  | 'resetStore'
  | 'addScore'
  | 'setScore'
  | 'startRound'
  | 'completeRound'
  | 'addRoundNote'
  | 'removeRoundNote'
  | 'addRoundTag'
  | 'removeRoundTag'
  | 'joinPlayer'
  | 'removePlayer'
  | 'reorderPlayers'
  | 'pauseSession'
  | 'resumeSession'
  | 'restartSession'
  | 'endSession'
  | 'setSessionWinner'
  | 'updateSessionName'
  | 'updateSessionNotes'
  | 'updateSessionGameConfig'
  | 'addSessionTag'
  | 'removeSessionTag'
  | 'deleteSession'
  | 'archiveSession'
  | 'unarchiveSession'
  | 'undo'
  | 'redo'
  | 'syncNow'
  | 'enableRealtime'
  | 'disableRealtime'
  | 'applyRemoteEvents'
  | 'generateInviteToken'
  | 'revokeInviteToken'
  | 'enableInviteLink'
  | 'disableInviteLink'
  | 'enablePublicSpectator'
  | 'disablePublicSpectator'
  | 'enablePublicPlayerJoin'
  | 'disablePublicPlayerJoin'
  | 'addImage'
  | 'removeImage'
  | 'updateImageCaption'
  | 'setPrimaryImage'
>;

type LoadTokenController = {
  value: number;
};

type SessionActionDependencies = {
  set: ImmerSetter;
  get: () => SessionStore;
  requireActiveSession: ReturnType<typeof createRequireActiveSession>;
  refreshSessionFromEvents: (sessionId: SessionId) => Promise<void>;
  updateUndoRedoAvailability: (sessionId: SessionId) => Promise<void>;
  operationQueue: OperationQueue;
  loadToken: LoadTokenController;
  getCurrentUserId: () => UserId | undefined;
};

const createQueueDeps = ({
  set,
  operationQueue,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
}: SessionActionDependencies): QueueDependencies => ({
  set,
  operationQueue,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
});

export const createSessionActions = (deps: SessionActionDependencies): SessionStoreActions => {
  const queueDeps = createQueueDeps(deps);

  return {
    loadSession: async (sessionId: SessionId) => {
      const requestToken = ++deps.loadToken.value;

      deps.set((draft) => {
        draft.isLoading = true;
        draft.error = undefined;
      });

      try {
        const events = await getSessionEvents(sessionId);
        const session = replaySessionEvents(events);
        const availability = computeUndoRedoAvailability(events);

        deps.set((draft) => {
          if (requestToken !== deps.loadToken.value) {
            return;
          }
          draft.activeSession = session;
          draft.isLoading = false;
          draft.canUndo = availability.canUndo;
          draft.canRedo = availability.canRedo;
        });

        const userId = deps.getCurrentUserId();
        if (userId) {
          void deps
            .get()
            .enableRealtime(sessionId)
            .catch((error) => {
              handleBackgroundError(error, { operation: 'enableRealtime', sessionId }, 'warn');
            });
        }
      } catch (error) {
        const errorMessage = handleUserFacingError(error, 'Failed to load session', {
          operation: 'loadSession',
          sessionId,
        });

        deps.set((draft) => {
          if (requestToken !== deps.loadToken.value) {
            return;
          }
          draft.error = errorMessage;
          draft.isLoading = false;
          draft.canUndo = false;
          draft.canRedo = false;
        });
      }
    },

    clearSession: () => {
      deps.set((draft) => {
        draft.activeSession = undefined;
        draft.error = undefined;
        draft.canUndo = false;
        draft.canRedo = false;
      });
    },

    resetStore: () => {
      deps.loadToken.value += 1;
      const subscriptions = [...deps.get().realtimeSubscriptions.values()];
      for (const subscription of subscriptions) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          handleBackgroundError(error, { operation: 'unsubscribeRealtime' }, 'warn');
        }
      }

      deps.set((draft) => {
        draft.activeSession = undefined;
        draft.error = undefined;
        draft.canUndo = false;
        draft.canRedo = false;
        draft.isLoading = false;
        draft.realtimeSubscriptions = new Map();
      });
    },

    addScore: async (playerId: PlayerId, roundId: RoundId, delta: number) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing']);
      const targetRound = findRoundOrThrow(session, roundId);

      if (targetRound.deletedAt) {
        throw new Error('Cannot modify a deleted round');
      }
      if (targetRound.completedAt) {
        throw new Error('Cannot modify scores for a completed round');
      }

      const userId = deps.getCurrentUserId();

      await enqueueScoreDelta({
        ...queueDeps,
        sessionId: session.id,
        roundId,
        playerId,
        delta,
        userId,
      });
    },

    setScore: async (playerId: PlayerId, roundId: RoundId, value: number) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing']);
      const targetRound = findRoundOrThrow(session, roundId);

      if (targetRound.deletedAt) {
        throw new Error('Cannot modify a deleted round');
      }

      const hasExistingScore = Object.prototype.hasOwnProperty.call(targetRound.scores, playerId);
      const previousScore = hasExistingScore ? targetRound.scores[playerId] : undefined;
      const userId = deps.getCurrentUserId();

      await enqueueScoreSet({
        ...queueDeps,
        sessionId: session.id,
        roundId,
        playerId,
        value,
        userId,
        hasExistingScore,
        previousScore,
      });
    },

    startRound: async (name?: string) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing']);

      const lastRound = session.rounds.at(-1);
      if (lastRound && !lastRound.completedAt) {
        throw new Error('Cannot start a new round before completing the current round');
      }

      const newRoundNumber = session.currentRoundNumber + 1;
      const roundId = createRoundId();
      const timestamp = Date.now();
      const userId = deps.getCurrentUserId();

      await enqueueStartRound({
        ...queueDeps,
        sessionId: session.id,
        roundId,
        roundNumber: newRoundNumber,
        previousRoundNumber: session.currentRoundNumber,
        name,
        userId,
        timestamp,
      });
    },

    completeRound: async (roundId: RoundId) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing']);
      const round = findRoundOrThrow(session, roundId);

      if (round.deletedAt) {
        throw new Error('Cannot complete a deleted round');
      }

      if (round.completedAt) {
        return;
      }

      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();

      await enqueueCompleteRound({
        ...queueDeps,
        sessionId: session.id,
        roundId,
        previousCompletedAt: round.completedAt,
        previousUpdatedAt: round.updatedAt,
        userId,
        timestamp,
      });
    },

    addRoundNote: async (roundId: RoundId, note: string) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);
      const round = findRoundOrThrow(session, roundId);

      if (round.deletedAt) {
        throw new Error('Cannot add note to a deleted round');
      }

      const previousNote = round.notes;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'addRoundNote',
        eventType: 'RoundNoteAdded',
        payload: { roundId, note },
        userId,
        dedupKey: `round:note:add:${session.id}:${roundId}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            applyRoundNoteToDraft(draft, session.id, roundId, note);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            applyRoundNoteToDraft(draft, session.id, roundId, previousNote);
          });
        },
      });
    },

    removeRoundNote: async (roundId: RoundId) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);
      const round = findRoundOrThrow(session, roundId);

      if (round.deletedAt) {
        throw new Error('Cannot remove note from a deleted round');
      }

      const previousNote = round.notes;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'removeRoundNote',
        eventType: 'RoundNoteRemoved',
        payload: { roundId },
        userId,
        dedupKey: `round:note:remove:${session.id}:${roundId}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            applyRoundNoteToDraft(draft, session.id, roundId);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            applyRoundNoteToDraft(draft, session.id, roundId, previousNote);
          });
        },
      });
    },

    addRoundTag: async (roundId: RoundId, tag: string) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);
      const round = findRoundOrThrow(session, roundId);

      if (round.deletedAt) {
        throw new Error('Cannot add tag to a deleted round');
      }

      const previousTags = round.tags ? [...round.tags] : undefined;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'addRoundTag',
        eventType: 'RoundTagAdded',
        payload: { roundId, tag },
        userId,
        dedupKey: `round:tag:add:${session.id}:${roundId}:${tag}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            addRoundTagToDraft(draft, session.id, roundId, tag);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setRoundTagsOnDraft(draft, session.id, roundId, previousTags);
          });
        },
      });
    },

    removeRoundTag: async (roundId: RoundId, tag: string) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);
      const round = findRoundOrThrow(session, roundId);

      if (round.deletedAt) {
        throw new Error('Cannot remove tag from a deleted round');
      }

      const previousTags = round.tags ? [...round.tags] : undefined;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'removeRoundTag',
        eventType: 'RoundTagRemoved',
        payload: { roundId, tag },
        userId,
        dedupKey: `round:tag:remove:${session.id}:${roundId}:${tag}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            removeRoundTagFromDraft(draft, session.id, roundId, tag);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setRoundTagsOnDraft(draft, session.id, roundId, previousTags);
          });
        },
      });
    },

    joinPlayer: async (playerId: PlayerId, entryRound?: number) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);

      const existingPlayer = session.players.find((p) => p.id === playerId && !p.deletedAt);
      if (existingPlayer) {
        throw new Error('Player is already in this session');
      }

      await validatePlayerExists(playerId);

      const player = await getPlayer(playerId);
      if (!player) {
        throw new Error(`Player ${playerId} was deleted`);
      }
      if (player.deletedAt) {
        throw new Error(`Player ${playerId} has been deleted`);
      }

      const allPlayers = session.players.filter((p) => !p.deletedAt);
      const assignedColor = player.color ?? assignPlayerColor(allPlayers);
      const playerWithColor: Player = { ...player, color: assignedColor };

      const roundNumber = entryRound ?? session.currentRoundNumber;
      const userId = deps.getCurrentUserId();
      const shouldPersistColor = !player.color && Boolean(playerWithColor.color);

      await enqueueJoinPlayer({
        ...queueDeps,
        sessionId: session.id,
        player: playerWithColor,
        entryRound: roundNumber,
        userId,
        shouldPersistColor,
      });
    },

    removePlayer: async (playerId: PlayerId) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);

      const player = session.players.find((p) => p.id === playerId && !p.deletedAt);
      if (!player) {
        throw new Error('Player is not in this session');
      }

      const activePlayers = session.players.filter((p) => !p.deletedAt);
      if (activePlayers.length <= 1) {
        throw new Error('Cannot remove the last player from a session');
      }

      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();

      await enqueueRemovePlayer({
        ...queueDeps,
        sessionId: session.id,
        playerId,
        previousPlayer: { ...player },
        userId,
        timestamp,
      });
    },

    reorderPlayers: async (playerIds: PlayerId[]) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);

      // Validate all active players are included
      const activePlayers = session.players.filter((p) => !p.deletedAt);
      const activePlayerIds = new Set(activePlayers.map((p) => p.id));
      const providedPlayerIds = new Set(playerIds);

      if (activePlayerIds.size !== providedPlayerIds.size) {
        throw new Error('All active players must be included in the reorder');
      }

      for (const playerId of activePlayerIds) {
        if (!providedPlayerIds.has(playerId)) {
          throw new Error(`Player ${playerId} is missing from the reorder`);
        }
      }

      // Get current order for rollback
      const previousOrder = activePlayers.map((p) => p.id);

      const userId = deps.getCurrentUserId();

      await enqueueReorderPlayers({
        ...queueDeps,
        sessionId: session.id,
        playerIds,
        previousOrder,
        userId,
      });
    },

    pauseSession: async () => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing']);

      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();

      await enqueuePauseSession({
        ...queueDeps,
        sessionId: session.id,
        previousStatus: session.status,
        previousPausedAt: session.pausedAt,
        userId,
        timestamp,
      });
    },

    resumeSession: async () => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['paused']);

      const userId = deps.getCurrentUserId();

      await enqueueResumeSession({
        ...queueDeps,
        sessionId: session.id,
        previousStatus: session.status,
        previousPausedAt: session.pausedAt,
        userId,
      });
    },

    restartSession: async () => {
      const session = deps.requireActiveSession();
      // Allow restart on ongoing, paused, or finished sessions
      ensureSessionStatus(session, ['ongoing', 'paused', 'finished']);

      const previousStatus = session.status;
      const previousPausedAt = session.pausedAt;
      const previousEndedAt = session.endedAt;
      const previousCurrentRoundNumber = session.currentRoundNumber;
      const userId = deps.getCurrentUserId();

      await enqueueRestartSession({
        ...queueDeps,
        sessionId: session.id,
        previousStatus,
        previousPausedAt,
        previousEndedAt,
        previousCurrentRoundNumber,
        userId,
      });
    },

    endSession: async () => {
      const session = deps.requireActiveSession();
      if (session.status === 'finished') {
        throw new Error('Session is already finished');
      }

      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();
      const currentRound = getCurrentRound(session);
      const shouldCompleteRound = Boolean(currentRound && !currentRound.completedAt);

      const sessionForWinner = shouldCompleteRound
        ? {
            ...session,
            rounds: session.rounds.map((round) =>
              round.id === currentRound?.id ? { ...round, completedAt: timestamp } : round,
            ),
          }
        : session;

      const result = calculateWinner(sessionForWinner, session.gameConfigSnapshot);
      const winner: PlayerOrTeamId | undefined =
        result.winners.length === 1 ? result.winners[0] : undefined;

      const events: NewSessionEvent[] = [];
      if (shouldCompleteRound && currentRound) {
        events.push(
          createSystemEvent(session.id, 'RoundCompleted', { roundId: currentRound.id }, userId),
        );
      }

      events.push(createSystemEvent(session.id, 'SessionEnded', {}, userId));

      if (winner) {
        events.push(
          createSystemEvent(
            session.id,
            'SessionWinnerSet',
            {
              winnerIds: [winner],
            },
            userId,
          ),
        );
      }

      await enqueueEndSession({
        ...queueDeps,
        sessionId: session.id,
        events,
        timestamp,
        winner,
        previousStatus: session.status,
        previousEndedAt: session.endedAt,
        previousWinner: session.winner,
        previousRoundState: currentRound
          ? {
              roundId: currentRound.id,
              completedAt: currentRound.completedAt,
              updatedAt: currentRound.updatedAt,
            }
          : undefined,
      });
    },

    setSessionWinner: async (winnerIds: PlayerOrTeamId[]) => {
      const session = deps.requireActiveSession();
      if (session.status !== 'finished') {
        throw new Error('Can only set winner for finished sessions');
      }

      const previousWinner = session.winner;
      const userId = deps.getCurrentUserId();
      const sortedWinnerIds = winnerIds.toSorted((a, b) => a.localeCompare(b));
      const dedupKey = `session:winner:${session.id}:${sortedWinnerIds.join(':')}`;

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'setSessionWinner',
        eventType: 'SessionWinnerSet',
        payload: { winnerIds },
        userId,
        dedupKey,
        priority: 4,
        optimisticUpdate: () => {
          deps.set((draft) => {
            setSessionWinnerOnDraft(
              draft,
              session.id,
              winnerIds.length === 1 ? winnerIds[0] : undefined,
            );
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setSessionWinnerOnDraft(draft, session.id, previousWinner);
          });
        },
      });
    },

    updateSessionName: async (name: string) => {
      const session = deps.requireActiveSession();
      const previousName = session.name;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'updateSessionName',
        eventType: 'SessionNameChanged',
        payload: { name },
        userId,
        dedupKey: `session:name:${session.id}`,
        priority: 5,
        optimisticUpdate: () => {
          deps.set((draft) => {
            setSessionNameOnDraft(draft, session.id, name);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setSessionNameOnDraft(draft, session.id, previousName);
          });
        },
      });
    },

    updateSessionNotes: async (notes: string) => {
      const session = deps.requireActiveSession();
      const previousNotes = session.notes;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'updateSessionNotes',
        eventType: 'SessionNotesChanged',
        payload: { notes },
        userId,
        dedupKey: `session:notes:${session.id}`,
        priority: 5,
        optimisticUpdate: () => {
          deps.set((draft) => {
            setSessionNotesOnDraft(draft, session.id, notes);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setSessionNotesOnDraft(draft, session.id, previousNotes);
          });
        },
      });
    },

    updateSessionGameConfig: async (config: GameConfig) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);

      const didChange = JSON.stringify(session.gameConfigSnapshot) !== JSON.stringify(config);
      if (!didChange) {
        return;
      }

      const previousConfig: GameConfig = structuredClone(session.gameConfigSnapshot);
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'updateSessionGameConfig',
        eventType: 'SessionGameConfigChanged',
        payload: { gameConfigSnapshot: config },
        userId,
        dedupKey: `session:config:${session.id}`,
        priority: 4,
        optimisticUpdate: () => {
          deps.set((draft) => {
            setSessionGameConfigOnDraft(draft, session.id, config);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setSessionGameConfigOnDraft(draft, session.id, previousConfig);
          });
        },
      });
    },

    addSessionTag: async (tag: string) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);

      if (tag.length === 0 || tag.length > 50) {
        throw new Error('Tag must be between 1 and 50 characters');
      }

      const currentTags = session.tags || [];
      if (currentTags.includes(tag)) {
        throw new Error('Tag already exists');
      }
      if (currentTags.length >= 5) {
        throw new Error('Maximum 5 tags allowed');
      }

      const previousTags = session.tags ? [...session.tags] : undefined;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'addSessionTag',
        eventType: 'SessionTagAdded',
        payload: { tag },
        userId,
        dedupKey: `session:tag:add:${session.id}:${tag}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            addSessionTagOnDraft(draft, session.id, tag);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setSessionTagsOnDraft(draft, session.id, previousTags);
          });
        },
      });
    },

    removeSessionTag: async (tag: string) => {
      const session = deps.requireActiveSession();
      ensureSessionStatus(session, ['ongoing', 'paused']);

      const currentTags = session.tags || [];
      if (!currentTags.includes(tag)) {
        throw new Error('Tag does not exist');
      }

      const previousTags = session.tags ? [...session.tags] : undefined;
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'removeSessionTag',
        eventType: 'SessionTagRemoved',
        payload: { tag },
        userId,
        dedupKey: `session:tag:remove:${session.id}:${tag}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            removeSessionTagOnDraft(draft, session.id, tag);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            setSessionTagsOnDraft(draft, session.id, previousTags);
          });
        },
      });
    },

    deleteSession: async () => {
      const session = deps.requireActiveSession();

      const events = await getSessionEvents(session.id);
      const activityResolver = createSessionEventActivityResolver(events);
      const deletedEvent = events.find(
        (event) => event.type === 'SessionDeleted' && activityResolver.isActive(event.id),
      );
      if (deletedEvent) {
        throw new Error('Session is already deleted');
      }

      const previousSession: Session = structuredClone(session);
      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'deleteSession',
        eventType: 'SessionDeleted',
        payload: {},
        userId,
        dedupKey: `session:delete:${session.id}`,
        priority: 3,
        refreshAfterSuccess: false,
        optimisticUpdate: () => {
          deps.set((draft) => {
            clearActiveSessionOnDraft(draft);
          });
        },
        rollback: () => {
          deps.set((draft) => {
            restoreActiveSessionOnDraft(draft, previousSession);
          });
        },
      });
    },

    archiveSession: async () => {
      const session = deps.requireActiveSession();
      if (session.archivedAt) {
        return;
      }

      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'archiveSession',
        eventType: 'SessionArchived',
        payload: { archivedAt: timestamp },
        userId,
        dedupKey: `session:archive:${session.id}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            if (!draft.activeSession || draft.activeSession.id !== session.id) {
              return;
            }
            draft.activeSession.archivedAt = timestamp;
          });
        },
        rollback: () => {
          deps.set((draft) => {
            if (!draft.activeSession || draft.activeSession.id !== session.id) {
              return;
            }
            draft.activeSession.archivedAt = undefined;
          });
        },
      });
    },

    unarchiveSession: async () => {
      const session = deps.requireActiveSession();
      if (!session.archivedAt) {
        return;
      }

      const userId = deps.getCurrentUserId();
      const previousArchivedAt = session.archivedAt;

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'unarchiveSession',
        eventType: 'SessionUnarchived',
        payload: {},
        userId,
        dedupKey: `session:unarchive:${session.id}`,
        optimisticUpdate: () => {
          deps.set((draft) => {
            if (!draft.activeSession || draft.activeSession.id !== session.id) {
              return;
            }
            draft.activeSession.archivedAt = undefined;
          });
        },
        rollback: () => {
          deps.set((draft) => {
            if (!draft.activeSession || draft.activeSession.id !== session.id) {
              return;
            }
            draft.activeSession.archivedAt = previousArchivedAt;
          });
        },
      });
    },

    undo: async () => {
      const session = deps.requireActiveSession();

      const events = await getSessionEvents(session.id);
      const undoableEvent = getUndoableEvent(events);

      if (!undoableEvent) {
        throw new Error('No undoable events available');
      }

      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'undo',
        eventType: 'EventUndone',
        payload: { undoneEventId: undoableEvent.id },
        userId,
        dedupKey: `session:undo:${session.id}:${undoableEvent.id}`,
        priority: 3,
        refreshAfterSuccess: true,
        optimisticUpdate: () => {},
        rollback: () => {},
      });
    },

    redo: async () => {
      const session = deps.requireActiveSession();

      const events = await getSessionEvents(session.id);
      const redoableEvent = getRedoableEvent(events);

      if (!redoableEvent) {
        throw new Error('No redoable events available');
      }

      const userId = deps.getCurrentUserId();

      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId: session.id,
        operationType: 'redo',
        eventType: 'EventRedone',
        payload: { redoneEventId: redoableEvent.id },
        userId,
        dedupKey: `session:redo:${session.id}:${redoableEvent.id}`,
        priority: 3,
        refreshAfterSuccess: true,
        optimisticUpdate: () => {},
        rollback: () => {},
      });
    },

    syncNow: async (sessionId: SessionId) => {
      try {
        const result = await syncSession(sessionId);
        console.info('Sync completed:', result);
        // Note: syncSession already calls applyRemoteEvents for downloaded events,
        // which uses refreshSessionFromEvents (incremental update, no flicker).
        // No need to call loadSession here - it would cause unnecessary flicker.
      } catch (error) {
        handleBackgroundError(error, { operation: 'syncSession', sessionId });
        throw error;
      }
    },

    enableRealtime: async (sessionId: SessionId) => {
      const currentSubscriptions = deps.get().realtimeSubscriptions;
      if (currentSubscriptions.has(sessionId)) {
        return;
      }
      const subscription = await subscribeToSession(sessionId, (event) => {
        void (async () => {
          try {
            // Apply remote events through the store's incremental refresh path
            // This updates the active session state without triggering a full
            // loading cycle, avoiding visible flicker in the UI.
            await deps.get().applyRemoteEvents([event]);
          } catch (error) {
            handleBackgroundError(error, { operation: 'mergeRemoteEvent', sessionId }, 'warn');
          }
        })();
      });
      deps.set((draft) => {
        draft.realtimeSubscriptions.set(sessionId, subscription);
      });
    },

    disableRealtime: (sessionId: SessionId) => {
      const currentSubscriptions = deps.get().realtimeSubscriptions;
      const subscription = currentSubscriptions.get(sessionId);
      if (subscription) {
        subscription.unsubscribe();
        deps.set((draft) => {
          draft.realtimeSubscriptions.delete(sessionId);
        });
      }
    },

    applyRemoteEvents: async (events: SessionEvent[]) => {
      if (events.length === 0) {
        return;
      }

      const sessionId = events[0]?.sessionId;
      const { activeSession } = deps.get();
      if (!activeSession || activeSession.id !== sessionId) {
        return;
      }

      await mergeRemoteEvents(events);

      await deps.refreshSessionFromEvents(activeSession.id);
      await deps.updateUndoRedoAvailability(activeSession.id);
    },

    // Invite token management
    generateInviteToken: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      const metadata = await getSessionMetadata(sessionId);
      const previousToken = metadata?.inviteToken ?? undefined;

      await enqueueGenerateInviteToken({
        ...queueDeps,
        sessionId,
        userId,
        previousToken,
      });
    },

    revokeInviteToken: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      const metadata = await getSessionMetadata(sessionId);
      if (!metadata?.inviteToken) {
        return;
      }

      await enqueueRevokeInviteToken({
        ...queueDeps,
        sessionId,
        userId,
        previousToken: metadata.inviteToken,
      });
    },

    enableInviteLink: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      await enqueueToggleInviteLink({
        ...queueDeps,
        sessionId,
        userId,
        enable: true,
      });
    },

    disableInviteLink: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      await enqueueToggleInviteLink({
        ...queueDeps,
        sessionId,
        userId,
        enable: false,
      });
    },

    // Visibility settings
    enablePublicSpectator: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId,
        operationType: 'enablePublicSpectator',
        eventType: 'SessionPublicSpectatorEnabled',
        payload: { enabledBy: userId },
        userId,
        dedupKey: `visibility:public-spectator:enable:${sessionId}`,
        priority: 6,
        optimisticUpdate: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicSpectator = true;
            }
          });
        },
        rollback: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicSpectator = false;
            }
          });
        },
      });
    },

    disablePublicSpectator: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId,
        operationType: 'disablePublicSpectator',
        eventType: 'SessionPublicSpectatorDisabled',
        payload: { disabledBy: userId },
        userId,
        dedupKey: `visibility:public-spectator:disable:${sessionId}`,
        priority: 6,
        optimisticUpdate: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicSpectator = false;
            }
          });
        },
        rollback: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicSpectator = true;
            }
          });
        },
      });
    },

    enablePublicPlayerJoin: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId,
        operationType: 'enablePublicPlayerJoin',
        eventType: 'SessionPublicPlayerJoinEnabled',
        payload: { enabledBy: userId },
        userId,
        dedupKey: `visibility:public-player-join:enable:${sessionId}`,
        priority: 6,
        optimisticUpdate: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicPlayerJoin = true;
            }
          });
        },
        rollback: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicPlayerJoin = false;
            }
          });
        },
      });
    },

    disablePublicPlayerJoin: async (sessionId: SessionId) => {
      const userId = deps.getCurrentUserId();
      await enqueueSessionEventOperation({
        ...queueDeps,
        sessionId,
        operationType: 'disablePublicPlayerJoin',
        eventType: 'SessionPublicPlayerJoinDisabled',
        payload: { disabledBy: userId },
        userId,
        dedupKey: `visibility:public-player-join:disable:${sessionId}`,
        priority: 6,
        optimisticUpdate: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicPlayerJoin = false;
            }
          });
        },
        rollback: () => {
          deps.set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.activeSession.isPublicPlayerJoin = true;
            }
          });
        },
      });
    },

    addImage: async (
      file: File,
      entityType: import('@/schemas/image').ImageEntityType,
      entityId: string,
      options?: { caption?: string; isPrimary?: boolean; sessionIdForPrivacy?: SessionId },
    ): Promise<import('@/schemas/ids').ImageId> => {
      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();
      const imageId = createImageId();

      // For gallery images, use the imageId itself as the entityId
      const finalEntityId = entityType === 'gallery' ? imageId : entityId;

      // Only use session events if there's an active session
      // For gallery images and player avatars outside sessions, store directly in Dexie
      const activeSession = deps.get().activeSession;
      const sessionId: SessionId | undefined = activeSession?.id;

      await enqueueAddImage({
        ...queueDeps,
        sessionId,
        imageId,
        entityType,
        entityId: finalEntityId,
        file,
        caption: options?.caption,
        isPrimary: options?.isPrimary,
        sessionIdForPrivacy: options?.sessionIdForPrivacy,
        userId,
        timestamp,
      });

      return imageId;
    },

    removeImage: async (imageId: import('@/schemas/ids').ImageId) => {
      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();

      // Get image record to determine if it's part of a session
      const { getImageById } = await import('@/lib/images/queries');
      const image = await getImageById(imageId);
      if (!image) {
        throw new Error(`Image ${imageId} not found`);
      }

      // Only use session events if there's an active session
      // For gallery images and player avatars outside sessions, just soft delete in Dexie
      const activeSession = deps.get().activeSession;
      const sessionId: SessionId | undefined = activeSession?.id;

      await enqueueRemoveImage({
        ...queueDeps,
        sessionId,
        imageId,
        userId,
        timestamp,
      });
    },

    updateImageCaption: async (imageId: import('@/schemas/ids').ImageId, caption: string) => {
      const userId = deps.getCurrentUserId();
      const timestamp = Date.now();

      // Get image record to determine if it's part of a session
      const { getImageById } = await import('@/lib/images/queries');
      const image = await getImageById(imageId);
      if (!image) {
        throw new Error(`Image ${imageId} not found`);
      }

      // Only use session events if there's an active session
      // For gallery images and player avatars outside sessions, just update in Dexie
      const activeSession = deps.get().activeSession;
      const sessionId: SessionId | undefined = activeSession?.id;

      await enqueueUpdateImageCaption({
        ...queueDeps,
        sessionId,
        imageId,
        caption,
        userId,
        timestamp,
      });
    },

    setPrimaryImage: async (
      entityType: 'player' | 'game' | 'team',
      entityId: string,
      imageId: import('@/schemas/ids').ImageId,
    ) => {
      const userId = deps.getCurrentUserId();

      // Get the image record to determine if it's part of a session
      const { getImageById } = await import('@/lib/images/queries');
      const image = await getImageById(imageId);
      if (!image) {
        throw new Error(`Image ${imageId} not found`);
      }

      // Only use session events if there's an active session
      // For gallery images and player avatars outside sessions, just update in Dexie
      const activeSession = deps.get().activeSession;
      const sessionId: SessionId | undefined = activeSession?.id;

      await enqueueSetPrimaryImage({
        ...queueDeps,
        sessionId,
        entityType,
        entityId,
        imageId,
        userId,
      });
    },
  };
};
